import React, { useRef, useState, useEffect, useMemo } from 'react';
import { connect } from 'umi';
import {
  Button,
  DatePicker,
  Radio,
  Tabs,
  Row,
  Col,
  Card,
  Progress,
} from 'kenshin';
import TablePro from '@/components/TablePro';
import moment from 'moment';
import {
  exportProjectStatistics,
  exportWorkPackageStatistics,
} from '@/_serveice/dataAnalyse';
import { downloadXlsx } from '@/_util/util';
import * as echarts from 'echarts';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const ProjectDeliveryAnalysis = (props) => {
  const {
    dispatch,
    dataAnalyse: {
      projectManagerCostChart,
      projectServiceCategoryCostChart,
      projectCustCostChart,
      projectDeliveryList,
    },
  } = props;

  const tableRef = useRef();
  const pieChartRef = useRef(null);
  const sunburstChartRef = useRef(null);
  const barChartRef = useRef(null);

  // 默认为年
  const [dateType, setDateType] = useState('year');
  // 默认当前年份范围（今年到今年）
  const [currentDate, setCurrentDate] = useState([moment(), moment()]);
  // 当前Tab：project 或 package
  const [activeTab, setActiveTab] = useState('project');
  // 动态表格列
  const [dynamicColumns, setDynamicColumns] = useState([]);

  // 根据日期类型和当前日期计算开始和结束时间
  const getDateRange = (type, date) => {
    let start, end;
    if (!date || !date[0] || !date[1]) {
      return { statBeginTime: '', statEndTime: '' };
    }

    if (type === 'year') {
      start = date[0].clone().startOf('year').format('YYYY-MM-DD');
      end = date[1].clone().endOf('year').format('YYYY-MM-DD');
    } else if (type === 'month') {
      start = date[0].clone().startOf('month').format('YYYY-MM-DD');
      end = date[1].clone().endOf('month').format('YYYY-MM-DD');
    } else if (type === 'quarter') {
      start = date[0].clone().startOf('quarter').format('YYYY-MM-DD');
      end = date[1].clone().endOf('quarter').format('YYYY-MM-DD');
    } else {
      // 日
      start = date[0].format('YYYY-MM-DD');
      end = date[1].format('YYYY-MM-DD');
    }
    return { statBeginTime: start, statEndTime: end };
  };

  // 获取图表数据
  const fetchChartData = () => {
    const { statBeginTime, statEndTime } = getDateRange(dateType, currentDate);
    const params = { statBeginTime, statEndTime };

    dispatch({
      type: 'dataAnalyse/getProjectManagerCostChart',
      payload: params,
    });
    dispatch({
      type: 'dataAnalyse/getProjectServiceCategoryCostChart',
      payload: params,
    });
    dispatch({ type: 'dataAnalyse/getProjectCustCostChart', payload: params });
  };

  // 监听日期变化，刷新图表和表格
  useEffect(() => {
    fetchChartData();
    if (tableRef.current) {
      tableRef.current.reload();
    }
  }, [dateType, currentDate]);

  // 监听Tab变化，刷新表格
  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.reload();
    }
  }, [activeTab]);

  // 渲染饼图 (项目经理)
  useEffect(() => {
    if (!pieChartRef.current || !projectManagerCostChart) return;
    const myChart = echarts.init(pieChartRef.current);
    const data = projectManagerCostChart.map((item) => ({
      name: item.projectManagerName,
      value: +item.consumedAmount,
    }));

    const option = {
      title: {
        text: '项目经理',
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b} : {c} ({d}%)',
      },
      legend: {
        top: 'bottom',
      },
      series: [
        {
          name: '消耗金额',
          type: 'pie',
          radius: '50%',
          data: data,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };
    myChart.setOption(option);

    // 添加 resize 监听
    const handleResize = () => myChart.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      myChart.dispose();
    };
  }, [projectManagerCostChart]);

  // 渲染旭日图
  useEffect(() => {
    if (!sunburstChartRef.current || !projectServiceCategoryCostChart) return;
    const myChart = echarts.init(sunburstChartRef.current);

    // 递归处理数据格式，将金额转换为数字，并处理多层级结构
    const formatData = (list) => {
      if (!list || !Array.isArray(list)) return [];
      return list.map((item) => {
        const children = formatData(item.children);
        const node = {
          name: item.servCatgName,
          value: Number(item.consumedAmount) || 0, // 确保转换为数字
        };
        // 如果有子节点，添加到children属性中
        if (children.length > 0) {
          node.children = children;
        }
        return node;
      });
    };

    const data = formatData(projectServiceCategoryCostChart);
    console.log('旭日图数据', data, projectServiceCategoryCostChart);

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: '{b} : {c}',
      },
      series: {
        type: 'sunburst',
        data: data,
        radius: ['15%', '80%'],
        itemStyle: {
          borderWidth: 2,
        },
      },
    };
    myChart.setOption(option);

    const handleResize = () => myChart.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      myChart.dispose();
    };
  }, [projectServiceCategoryCostChart]);

  // 渲染柱状图 (公司名)
  useEffect(() => {
    if (!barChartRef.current || !projectCustCostChart) return;
    const myChart = echarts.init(barChartRef.current);

    const custNames = projectCustCostChart.map((item) => item.custName);
    const amounts = projectCustCostChart.map((item) => item.consumedAmount);

    const option = {
      title: {
        text: '汇总',
        left: 'center',
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        boundaryGap: [0, 0.01],
      },
      yAxis: {
        type: 'category',
        data: custNames,
      },
      series: [
        {
          name: '消耗金额',
          type: 'bar',
          data: amounts,
        },
      ],
    };
    myChart.setOption(option);

    const handleResize = () => myChart.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      myChart.dispose();
    };
  }, [projectCustCostChart]);

  // 基础列
  const baseColumns = [
    {
      title: '合同编号',
      dataIndex: 'contractNumber',
      key: 'contractNo',
      width: 120,
    },
    {
      title: '项目名称',
      dataIndex: 'projectName',
      key: 'projectName',
      width: 150,
    },
    {
      title: '客户名称',
      dataIndex: 'custCompFullName',
      key: 'custCompFullName',
      width: 150,
    },
    // { title: '业务人员', dataIndex: 'projectManagerName', key: 'projectManagerName', width: 100 },
    {
      title: '项目经理',
      dataIndex: 'projectManagerName',
      key: 'projectManagerName',
      width: 100,
    },
    {
      title: '分类',
      dataIndex: 'projectTypeFirstName',
      key: 'projectTypeFirstName',
      width: 100,
    },
    {
      title: '业务类型',
      dataIndex: 'projectTypeSecondName',
      key: 'projectTypeSecondName',
      width: 100,
    },
    {
      title: '项目状态',
      dataIndex: 'projectProgressName',
      key: 'projectProgressName',
      width: 100,
    },
    {
      title: '合同金额',
      dataIndex: 'projectAmount',
      key: 'projectProgressName',
      width: 100,
    },
    {
      title: '已消耗',
      dataIndex: 'consumedAmount',
      key: 'consumedAmount',
      width: 100,
    },
    {
      title: '未消耗',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      width: 100,
    },
    {
      title: '消耗进度',
      dataIndex: 'projectActualConsumProgressValue',
      key: 'projectActualConsumProgressValue',
      width: 200,
      render: (v, row) => {
        const status = {
          0: 'exception',
          2: 'success',
        };
        // 0: 滞后(red/exception), 1: 正常(blue/default), 2: 超前(green/success)
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: 5 }}>{v}%</span>
            <Progress
              style={{ flex: 1 }}
              percentage={v}
              status={status[row.projectProgress]}
              strokeWidth={6}
              showText={false}
            />
          </div>
        );
      },
    },
  ];

  // 工作包列
  const workPackageColumns = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (text, record, index) => index + 1,
    },
    {
      title: '合同编号',
      dataIndex: 'contractNumber',
      key: 'contractNumber',
      width: 120,
    },
    {
      title: '客户名称',
      dataIndex: 'custCompFullName',
      key: 'custCompFullName',
      width: 150,
    },
    {
      title: '项目名称',
      dataIndex: 'projectName',
      key: 'projectName',
      width: 150,
    },
    {
      title: '一级分类',
      dataIndex: 'firstServCatgName',
      key: 'firstServCatgName',
      width: 100,
    },
    {
      title: '二级分类',
      dataIndex: 'secondServCatgName',
      key: 'secondServCatgName',
      width: 100,
    },
    {
      title: '工作包名称',
      dataIndex: 'firstProServCatgName',
      key: 'firstProServCatgName',
      width: 150,
    },
    // {
    //   title: '二级工作包',
    //   dataIndex: 'secondProServCatgName',
    //   key: 'secondProServCatgName',
    //   width: 100,
    // },
    // {
    //   title: '三级工作包',
    //   dataIndex: 'thirdProServCatgName',
    //   key: 'thirdProServCatgName',
    //   width: 100,
    // },
    {
      title: '责任人',
      dataIndex: 'servCatgLeaderName',
      key: 'servCatgLeaderName',
      width: 100,
    },
    {
      title: '项目团队',
      dataIndex: 'teamMembers',
      key: 'teamMembers',
      width: 150,
    },
    {
      title: '数量',
      dataIndex: 'closeCount',
      key: 'closeCount',
      width: 80,
    },
    {
      title: '完成金额',
      dataIndex: 'consumedAmount',
      key: 'consumedAmount',
      width: 100,
    },
  ];

  // 搜索列配置
  const searchCloumns = [
    // {
    //   name: 'projectName',
    //   type: 'Input',
    //   props: {
    //     placeholder: '输入项目名称模糊查询',
    //   },
    // },
  ];

  // 处理表格数据变化，生成动态列
  useEffect(() => {
    if (
      projectDeliveryList &&
      projectDeliveryList.datas &&
      projectDeliveryList.datas.length > 0
    ) {
      const firstRow = projectDeliveryList.datas[0];
      if (
        firstRow.serviceCategoryConsumeList &&
        firstRow.serviceCategoryConsumeList.length > 0
      ) {
        const extraColumns = firstRow.serviceCategoryConsumeList.map(
          (item) => ({
            title: item.extendData,
            dataIndex: item.extendData,
            key: item.extendData,
            width: 120,
            render: (_, record) => {
              const found = record.serviceCategoryConsumeList?.find(
                (c) => c.extendData === item.extendData,
              );
              return found ? found.value : 0;
            },
          }),
        );
        setDynamicColumns([...baseColumns, ...extraColumns]);
      } else {
        setDynamicColumns(baseColumns);
      }
    } else {
      setDynamicColumns(baseColumns);
    }
  }, [projectDeliveryList]);

  // 处理搜索提交
  const onSubmit = (values) => {
    const { statBeginTime, statEndTime } = getDateRange(dateType, currentDate);
    return {
      ...values,
      statBeginTime,
      statEndTime,
    };
  };

  // 导出
  const handleExport = () => {
    const formValues = tableRef.current?.searchRef?.current?.value || {};
    const { statBeginTime, statEndTime } = getDateRange(dateType, currentDate);

    const params = {
      ...formValues,
      statBeginTime,
      statEndTime,
    };

    const url =
      activeTab === 'project'
        ? exportProjectStatistics
        : exportWorkPackageStatistics;
    downloadXlsx(
      url,
      activeTab === 'project' ? '项目交付数据分析' : '工作包交付数据分析',
      'post',
      params,
    );
  };

  const extra = () => [
    <Button key="export" type="primary" onClick={handleExport}>
      导出数据
    </Button>,
  ];

  // 顶部日期切换区域
  const renderDateFilter = () => (
    <div style={{ marginBottom: 16 }}>
      <Radio.Group
        value={dateType}
        onChange={(e) => {
          setDateType(e.target.value);
          setCurrentDate([moment(), moment()]);
        }}
        buttonStyle="solid"
      >
        <Radio.Button value="month">月</Radio.Button>
        <Radio.Button value="quarter">季度</Radio.Button>
        <Radio.Button value="year">年</Radio.Button>
      </Radio.Group>
      <div style={{ marginTop: 16 }}>
        <span style={{ marginRight: 8 }}>日期范围:</span>
        <RangePicker
          picker={
            dateType === 'quarter'
              ? 'quarter'
              : dateType === 'year'
              ? 'year'
              : 'month'
          }
          value={currentDate}
          onChange={(val) => {
            if (val) setCurrentDate(val);
          }}
          allowClear={false}
        />
      </div>
    </div>
  );

  return (
    <div style={{ padding: 20, background: '#fff' }}>
      {renderDateFilter()}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: 20 }}
      >
        <TabPane tab="项目" key="project" />
        <TabPane tab="工作包" key="package" />
      </Tabs>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card title="求和项：消耗金额" bordered={false}>
            <div ref={pieChartRef} style={{ width: '100%', height: 300 }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <div
              ref={sunburstChartRef}
              style={{ width: '100%', height: 300 }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false}>
            <div ref={barChartRef} style={{ width: '100%', height: 300 }} />
          </Card>
        </Col>
      </Row>

      <TablePro
        actionRef={(ref) => (tableRef.current = ref)}
        request={
          activeTab === 'project'
            ? 'dataAnalyse/getProjectStatisticsPage'
            : 'dataAnalyse/getWorkPackageStatisticsPage'
        }
        data={projectDeliveryList}
        columns={
          activeTab === 'project'
            ? dynamicColumns.length > 0
              ? dynamicColumns
              : baseColumns
            : workPackageColumns
        }
        searchCloumns={searchCloumns}
        onSubmit={onSubmit}
        extra={extra}
        scrollX
        curColumns
        rowKey={(record, index) => index}
      />
    </div>
  );
};

export default connect(({ dataAnalyse }) => ({
  dataAnalyse,
}))(ProjectDeliveryAnalysis);
